import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  customOrdersApi,
  type CustomOrderDetail,
  type Guardrails,
  type ListCustomOrdersParams,
} from '../api/customOrdersApi';
import { useToast } from '../../../shared/components/toast/useToast';

const errMsg = (err: unknown, fallback: string) => {
  const msg = (err as { message?: unknown })?.message;
  if (Array.isArray(msg)) return (msg as string[])[0] || fallback;
  return (typeof msg === 'string' ? msg : '') || fallback;
};

const LIST = 'custom-orders';
const DETAIL = 'custom-order';
const GUARDRAILS = 'custom-order-guardrails';

/** Tenant-scoped list. Disabled until a merchant is selected; cache keyed per tenant + filters. */
export const useCustomOrders = (
  tenantId: string | null | undefined,
  params: ListCustomOrdersParams,
) =>
  useQuery({
    queryKey: [LIST, tenantId, params],
    queryFn: () => customOrdersApi.list(params),
    enabled: !!tenantId,
    staleTime: 30 * 1000,
  });

/** Single order detail (assets carry signed read urls from the BFF). */
export const useCustomOrder = (id: string | null | undefined) =>
  useQuery({
    queryKey: [DETAIL, id],
    queryFn: () => customOrdersApi.get(id as string),
    enabled: !!id,
  });

export const useSetPricing = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, amountMinor, currency }: { id: string; amountMinor: number; currency?: string }) =>
      customOrdersApi.setPricing(id, amountMinor, currency),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: [DETAIL, id] });
      queryClient.invalidateQueries({ queryKey: [LIST] });
      toast.success('Price saved');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to save price')),
  });
};

/**
 * Mark a priced draft ready (→ AWAITING_PAYMENT). Optimistically flips the detail
 * cache so the UI reflects the new state immediately, and rolls back on failure.
 */
export const useMarkReady = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (id: string) => customOrdersApi.markReady(id),
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: [DETAIL, id] });
      const previous = queryClient.getQueryData<CustomOrderDetail>([DETAIL, id]);
      if (previous) {
        queryClient.setQueryData<CustomOrderDetail>([DETAIL, id], {
          ...previous,
          status: 'AWAITING_PAYMENT',
        });
      }
      return { previous };
    },
    onError: (err: unknown, id, context) => {
      if (context?.previous) queryClient.setQueryData([DETAIL, id], context.previous);
      toast.error(errMsg(err, 'Failed to mark ready'));
    },
    onSuccess: () => toast.success('Order marked ready — invoice issued'),
    onSettled: (_data, _err, id) => {
      queryClient.invalidateQueries({ queryKey: [DETAIL, id] });
      queryClient.invalidateQueries({ queryKey: [LIST] });
    },
  });
};

export const useGuardrails = (tenantId: string | null | undefined) =>
  useQuery({
    queryKey: [GUARDRAILS, tenantId],
    queryFn: () => customOrdersApi.getGuardrails(),
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
  });

export const useSetGuardrails = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (body: Guardrails) => customOrdersApi.setGuardrails(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [GUARDRAILS] });
      toast.success('Upload limits saved');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to save upload limits')),
  });
};
